import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useMemo, useState} from 'react';

import {useDataGridContext} from '../../context/useDataGridContext';
import {selectHorizontalSeparatorsVisible} from '../../store/createEagGridStore';
import {
  useAllFilteredSelected,
  useActionsDefinition,
  useGridSettingsSafe,
} from '../../store/selectors';
import {useEagGridSelector} from '../../store/useEagGridSelector';
import type {ActionsAlignment} from '../../types/ActionsAlignment';
import type {CellRendererType, EagColumn} from '../../types/EagColumn';
import type {EagColumnState} from '../../types/EagColumnState';
import type {RowData} from '../../types/RowData';
import {getValueFromRawCell} from '../../utils/getValueFromRawCell';
import {LineHeightToPx} from '../../utils/lineHeightToValue';
import {GridRow} from './components/GridRow/GridRow';
import {MobileRowActionSheet} from './components/MobileRowActionSheet/MobileRowActionSheet';
import {SkeletonRow} from './SkeletonRow';

export type GridBodyProps = {
  columnStates: EagColumnState[];
  columnStateMap: Map<string, EagColumnState>;
  nonRightPinnedColumns: EagColumn[];
  rightPinnedColumns: EagColumn[];
  visibleColumnIndexMap: Map<string, number>;
  rows: RowData[];
  /** Snapshot of the rows shown before the current refetch — kept visible when hasSilentRefetching. */
  previousRows: RowData[];
  skeletonRowCount: number;
  isLoading: boolean;
  /** When true, keep previous rows visible during a refetch instead of showing skeletons. */
  hasSilentRefetching: boolean;
  selectedRowIds: Set<string>;
  onRowClick?: (row: RowData, event: React.MouseEvent) => void;
  onRowContextMenu?: (row: RowData, event: React.MouseEvent) => void;
  pinnedLeftOffsets: Map<string, number>;
  pinnedRightOffsets: Map<string, number>;
  stripedRows?: boolean;
  lastPinnedLeftKey: string | null;
  firstPinnedRightKey: string | null;
  // Action column props — only present when action column is enabled
  onActionClick?: (actionKey: string, row: RowData, event: React.MouseEvent) => void;
  actionColumnWidth?: number;
  isMobile?: boolean;
  disableAllActions?: boolean;
  actionsAlignment?: ActionsAlignment | null;
  // Selection props — controls whether rows render a RowSelectCheckbox
  rowSelectMode: 'SINGLE' | 'MULTIPLE' | 'NONE';
  onToggleSelection?: (rowId: string) => void;
};

export function GridBody(props: GridBodyProps) {
  const allFilteredSelected = useAllFilteredSelected();
  const skeletonIndices = Array.from({length: props.skeletonRowCount}, (_, i) => i);
  const gridSettings = useGridSettingsSafe();
  const skeletonHeightPx = LineHeightToPx[gridSettings.rowHeight];
  const horizontalSeparators = useEagGridSelector(selectHorizontalSeparatorsVisible);
  const actionsDefinition = useActionsDefinition();
  const isRowHighlighted = useDataGridContext()?.isRowHighlighted;

  // GridBody owns the mobile action-sheet state — a single sheet instance shared by every
  // row, rather than one per row. Rows just call onOpenActionSheet(row) to populate it.
  const [actionSheetRow, setActionSheetRow] = useState<RowData | null>(null);
  // Re-resolve the row from the live `rows` prop instead of trusting the snapshotted
  // `actionSheetRow` state — keeps the sheet's data fresh across silent refetches and
  // makes the sheet disappear (rather than showing stale data) if the row is gone.
  const liveActionSheetRow = actionSheetRow
    ? (props.rows.find((row) => row.id === actionSheetRow.id) ?? null)
    : null;
  // Render-phase state reset (React's "adjusting state when props change" pattern): when the
  // sheet is hidden by anything other than an explicit close — its row vanished from `rows`
  // or the viewport left mobile — the stale actionSheetRow must be cleared too, or a later
  // fetch containing the same row id (or flipping back to mobile) reopens the sheet uninvited.
  if (actionSheetRow !== null && (props.isMobile !== true || liveActionSheetRow === null)) {
    setActionSheetRow(null);
  }
  const firstColumn = props.nonRightPinnedColumns[0];
  const rawTitleValue =
    liveActionSheetRow && firstColumn
      ? getValueFromRawCell(firstColumn, liveActionSheetRow[firstColumn.field])
      : null;
  // Only primitives are safe to stringify: FLAG returns the whole raw cell object and
  // MONEY/TAG/… values are objects, which would render as "[object Object]".
  const actionSheetTitle =
    typeof rawTitleValue === 'string' || typeof rawTitleValue === 'number'
      ? String(rawTitleValue)
      : '';

  const columnTypeMap = useMemo((): Map<string, CellRendererType> => {
    const map = new Map<string, CellRendererType>();
    for (const col of props.nonRightPinnedColumns) {
      map.set(col.key, col.type);
    }
    for (const col of props.rightPinnedColumns) {
      map.set(col.key, col.type);
    }
    return map;
  }, [props.nonRightPinnedColumns, props.rightPinnedColumns]);

  // Stale-while-revalidate: when opted in via hasSilentRefetching, keep the snapshotted
  // previous rows on screen during a refetch instead of flashing skeletons. The first load
  // (no previous rows) still shows skeletons. The loading bar in the header keeps signaling
  // activity because isLoading stays true throughout.
  const showStaleRows =
    props.isLoading && props.hasSilentRefetching && props.previousRows.length > 0;
  const showSkeletons = props.isLoading && !showStaleRows;
  const isEmpty = props.rows.length === 0 && !props.isLoading;
  const rowsToRender = showStaleRows ? props.previousRows : props.rows;

  return (
    <>
      {match({showSkeletons, isEmpty})
        .with({showSkeletons: true}, () => (
          <tbody>
            <tr role="status" className="sr-only" aria-label="Loading" />
            {skeletonIndices.map((index) => (
              <SkeletonRow
                key={index}
                heightPx={skeletonHeightPx}
                columnStates={props.columnStates}
                columnTypeMap={columnTypeMap}
                horizontalSeparators={horizontalSeparators}
                actionColumnWidth={props.actionColumnWidth}
                rowSelectMode={props.rowSelectMode}
              />
            ))}
          </tbody>
        ))
        .with({isEmpty: true}, () => <tbody />)
        .otherwise(() => (
          <tbody>
            <Show when={showStaleRows}>
              <tr role="status" className="sr-only" aria-label="Loading" />
            </Show>
            {rowsToRender.map((row, index) => (
              <GridRow
                key={row.id}
                columnStateMap={props.columnStateMap}
                nonRightPinnedColumns={props.nonRightPinnedColumns}
                rightPinnedColumns={props.rightPinnedColumns}
                visibleColumnIndexMap={props.visibleColumnIndexMap}
                row={row}
                isSelected={allFilteredSelected || props.selectedRowIds.has(row.id)}
                isHighlighted={isRowHighlighted?.(row) ?? false}
                onRowClick={props.onRowClick}
                pinnedLeftOffsets={props.pinnedLeftOffsets}
                pinnedRightOffsets={props.pinnedRightOffsets}
                isStriped={props.stripedRows && index % 2 === 1}
                rowIndex={index}
                lastPinnedLeftKey={props.lastPinnedLeftKey}
                firstPinnedRightKey={props.firstPinnedRightKey}
                onActionClick={props.onActionClick}
                onRowContextMenu={props.onRowContextMenu}
                actionColumnWidth={props.actionColumnWidth}
                isMobile={props.isMobile}
                disableAllActions={props.disableAllActions}
                actionsAlignment={props.actionsAlignment}
                rowSelectMode={props.rowSelectMode}
                onToggleSelection={props.onToggleSelection}
                onOpenActionSheet={props.isMobile ? setActionSheetRow : undefined}
              />
            ))}
          </tbody>
        ))}
      {/* Hoisted out of the tbody match() above: the sheet portals to document.body (see
          MobileSheet), so it's DOM-safe as a fragment sibling of the <tbody>. Keeping it
          outside the match() means a refetch that flips showSkeletons/isEmpty no longer
          unmounts+remounts the sheet, and gating on isMobile keeps it from staying mounted
          across a breakpoint flip. liveActionSheetRow (re-resolved above) makes the sheet
          close itself if its row vanishes from `rows` instead of showing stale data. */}
      {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
      {props.isMobile === true &&
        liveActionSheetRow &&
        props.onActionClick &&
        actionsDefinition && (
          <MobileRowActionSheet
            row={liveActionSheetRow}
            title={actionSheetTitle}
            actionsDefinition={actionsDefinition}
            onActionClick={props.onActionClick}
            onClose={() => setActionSheetRow(null)}
            isDisabled={props.disableAllActions}
            data-testid="eagGrid"
          />
        )}
    </>
  );
}
